import { useState, useEffect, Fragment } from "react";
import { fieldType } from "../../../../types/form";
import Select from "react-dropdown-select";
import "./peoplePicker.scss";
import { selectCurrentUser } from "../../../../modules/users/selector";
import { SelectedCurrentUser } from "../../../../modules/users/types";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { clearSearch, searchPeople } from "./actions";
import { searchPeopleSelector } from "./selectors";
import Loading from "src/components/Loading";
import { UserAvatar } from "../../avatar/Avatar";
import { twMerge } from "tailwind-merge";
import { SubResetFilter } from "src/pages/DashboardPage/mytasks/meetingsPage/components/MeetingCard";
import { TGDisclosure } from "../../TGDisclosure";
import { isAndroidWebView } from "src/utils/mobile";

type Props = {
  dataField: fieldType;
  onChange: (data: any) => void;
  value: any;
  errors?: any;
  type: string;
};

const getByPath = (object: any, path: any) => {
  if (!path) {
    return;
  }

  return path.split(".").reduce((acc: any, value: any) => acc[value], object);
};

function PeoplePicker(props: Props) {
  const { dataField, onChange, value, errors, type } = props;
  const currentUser: SelectedCurrentUser = useAppSelector((state) =>
    selectCurrentUser(state)
  );
  const dispatch = useAppDispatch();
  const people: any = useAppSelector((state) => searchPeopleSelector(state));
  const status: string = useAppSelector((state) => state.searchUsers.status);
  const [defaultValue, setDefaultValue] = useState<any>([]);
  const [searchString, setSearchString] = useState("");
  const [fieldValue, setFieldValue] = useState<any>([]);
  const [checkedEmployee, setCheckedEmployee] = useState(false);
  const [isCompactMobileView, setIsCompactMobileView] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 640px)");

    const updateMatch = () => {
      setIsCompactMobileView(mediaQuery.matches);
    };

    updateMatch();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMatch);
      return () => mediaQuery.removeEventListener("change", updateMatch);
    }

    mediaQuery.addListener(updateMatch);
    return () => mediaQuery.removeListener(updateMatch);
  }, []);

  const shouldHideLoadingIndicator = isCompactMobileView || isAndroidWebView();

  useEffect(() => {
    if (dataField.current) {
      setDefaultValue([
        {
          value: currentUser.email,
          label: currentUser.displayName,
          avatar: currentUser.avatar,
          email: currentUser.email,
          id: currentUser.id,
        },
      ]);
      setFieldValue([
        {
          value: currentUser.email,
          label: currentUser.displayName,
          avatar: currentUser.avatar,
          email: currentUser.email,
        },
      ]);
    }
  }, [JSON.stringify(dataField), type, JSON.stringify(currentUser)]);

  useEffect(() => {
    if (searchString.length >= 3) {
      dispatch(searchPeople(searchString));
    }
  }, [searchString]);

  useEffect(() => {
    if (fieldValue.length > 0) {
      if (fieldValue[0]["value"].toLowerCase() !== currentUser.email) {
        setCheckedEmployee(false);
      } else {
        setCheckedEmployee(true);
      }
      if (type === "approvers_replaced") {
        if (fieldValue[0]["value"].toLowerCase() === currentUser.email) {
          onChange(null);
        } else {
          onChange(fieldValue);
        }
      } else {
        onChange(fieldValue);
      }
    }
  }, [fieldValue]);

  const onSearch = ({ props, state, methods }: any) => {
    if (
      dataField.single &&
      (!dataField.options || dataField.options.length === 0)
    ) {
      if (state.search.length >= 3) {
        if (state.search !== searchString) {
          setSearchString(state.search);
        }
      }
    } else {
      const regexp = new RegExp(methods.safeString(state.search), "i");
      return methods
        .sortBy()
        .filter((item: any) =>
          regexp.test(
            getByPath(item, props.searchBy) || getByPath(item, props.valueField)
          )
        );
    }
  };

  const customContentRenderer = ({ props, state, methods }: any) => {
    return (
      <>
        {(state.values.length > 0 &&
          state.values.map((option: any) => (
            <span key={option.value} className="rounded p-1 flex flex-row items-center bg-white sm:bg-[#F3F4F6]">
              <UserAvatar
                avatar={option.avatar}
                shortName={option.label ? option.label.split(" ").filter(Boolean).slice(0, 2).map((word: string) => word[0]).join("") : ''}

                displayName={option.label}
                email={option.value}
              />
              <span className="block mx-2.5">{option.label}</span>
              {dataField.single && (
                <span onClick={(e) => {
                  setDefaultValue([]);
                  onChange(null)
                }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                </span>
              )}
              {!dataField.single && (
                <span onClick={(e) => methods.addItem(option)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M20.707 4.70697L19.293 3.29297L12 10.586L4.70697 3.29297L3.29297 4.70697L10.586 12L3.29297 19.293L4.70697 20.707L12 13.414L19.293 20.707L20.707 19.293L13.414 12L20.707 4.70697Z"
                      fill="#9CA3AF"
                    />
                  </svg>
                </span>
              )}
            </span>
          )))}
        {!state.values.length &&
          <input
            value={state.search || ""}
            onChange={(e) => methods.setSearch(e)}
            placeholder={dataField.placeholder}
            className="p-0 flex-1 min-w-[120px] border-0 outline-none bg-transparent focus:ring-0 focus:border-transparent placeholder:text-[#9CA3AF] sm:text-base sm:leading-6"
          />
        }

      </>
    );
  };

  const customDropdownRenderer = ({ props, state, methods }: any) => {
    const regexp = new RegExp(state.search, "i");
    return (
      <>
        {(state.search.length >= 3 && status === "SUCCESS" && props.options.length === 0) || props.options.length > 0 &&
          <div className="relative p-5 shadow-custom w-full">
            {state.search.length >= 3 &&
              status === "SUCCESS" &&
              props.options.length === 0 && (
                <span>
                  Нет совпадений по запросу <strong>{state.search}</strong>
                </span>
              )}

            {props.options
              .filter((item: any) =>
                regexp.test(item[props.searchBy] || item[props.labelField])
              )
              .map((option: any) => {
                if (!props.keepSelectedInList && methods.isSelected(option)) {
                  return null;
                }
                return (
                  <div className="mb-2" key={option[props.valueField]}>
                    {option.disabled ? (
                      <div className="cursor-not-allowed ml-7">
                        <span className="flex flex-row items-center">
                          <UserAvatar
                            avatar={option.avatar}
                            shortName={
                              option.label &&
                              option.label.split(" ")[0][0] +
                              option.label.split(" ")[1][0]
                            }
                            displayName={option.label}
                            email={option.value}
                          />
                          &nbsp;{option.label}
                        </span>
                      </div>
                    ) : (
                      <div
                        className="flex flex-row items-center item-renderer"
                        onClick={(e) =>
                          option.disabled ? null : methods.addItem(option)
                        }
                      >
                        {!dataField.single && (
                          <input
                            className="h-5 w-5 mr-2 rounded border-gray-300 text-[#012169]"
                            onChange={() =>
                              option.disabled ? undefined : methods.addItem(option)
                            }
                            type="checkbox"
                            checked={state.values.some(
                              (selectedOption: any) =>
                                selectedOption.value === option.value
                            )}
                          />
                        )}
                        <span className="flex flex-row items-center">
                          <UserAvatar
                            avatar={option.avatar}
                            shortName={
                              option.label &&
                              option.label.indexOf("@") === -1 &&
                              option.label.split(" ")[0][0] +
                              option.label.split(" ")[1][0]
                            }
                            displayName={option.label}
                            email={option.value}
                          />
                          &nbsp;{option.label}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        }
      </>
    );
  };

  return (
    <>
      {(dataField.isFilter && (
        <TGDisclosure
          wrapperClassName={`${dataField.width} relative`}
          title={dataField.title}
          count={(value && value.length) || 0}
        >
          <div
            className={twMerge(
              dataField.width,
              "custom-form",
              dataField.disabled && "cursor-not-allowed"
            )}
          >
            {(dataField.key === "ReportEmployee" || dataField.key === "AuthorEmailOWD" || dataField.key === "TestedEmployee") && (
              <label
                className={twMerge(
                  "flex text-black cursor-pointer select-none mb-5",
                  dataField.disabled ? "cursor-not-allowed" : ""
                )}
              >
                <div className="relative">
                  <input
                    disabled={dataField.disabled}
                    type="radio"
                    onChange={(e) => {
                      setCheckedEmployee(e.target.checked);
                      onChange(
                        e.target.checked
                          ? [
                            {
                              value: currentUser.email,
                              label: currentUser.displayName,
                              avatar: currentUser.avatar,
                              email: currentUser.email,
                            },
                          ]
                          : null
                      );
                    }}
                    className="sr-only"
                  />
                  <div
                    className={twMerge(
                      "flex items-center justify-center w-5 h-5 mr-4 border rounded-full box border-stroke",
                      dataField.disabled ? "border-gray3 bg-[#DFE4EA]" : "",
                      checkedEmployee ? "border-[#012169]" : ""
                    )}
                  >
                    <span
                      className={twMerge(
                        "h-[10px] w-[10px] rounded-full",
                        checkedEmployee ? "bg-darkblue" : "bg-transparent"
                      )}
                    ></span>
                  </div>
                </div>
                Я
              </label>
            )}
            <Select
              values={value || defaultValue}
              multi={!dataField.single}
              contentRenderer={customContentRenderer}
              dropdownRenderer={(props) => (
                <div
                  className={
                    dataField.single
                      ? "react-dropdown-select-dropdown no-border"
                      : "react-dropdown-select-dropdown"
                  }
                >
                  {customDropdownRenderer(props)}
                </div>
              )}
              options={
                (dataField.single &&
                  (!dataField.options || dataField.options.length === 0) &&
                  people) ||
                dataField.options
              }
              searchFn={onSearch}
              onClearAll={() => onChange(null)}
              clearable={
                (type === "audit_filters_my_tasks" && value && true) || false
              }
              disabled={dataField.disabled}
              onChange={(value) => {
                if (dataField.single) {
                  dispatch(clearSearch());
                }
                setFieldValue(value)
              }}
              loading={status === "LOADING"}
              loadingRenderer={shouldHideLoadingIndicator ? () => null : () => <Loading />}
            />
            {dataField.notice && (
              <div className="text-sm text-[#6B7280]">{dataField.notice}</div>
            )}
          </div>
          <SubResetFilter
            resetFilter={() => {
              setCheckedEmployee(false);
              onChange([]);
            }}
          />
        </TGDisclosure>
      )) || (
          <div
            className={twMerge(
              dataField.width,
              "mb-5 custom-form",
              dataField.disabled && "cursor-not-allowed"
            )}
          >
            {type !== "history" && dataField.title && (
              <label className="flex flex-row text-sm font-medium leading-6 text-gray-900 mb-2.5 gap-2">
                <span>{dataField.title}</span>
                {dataField.require && (
                  <div className="flex flex-row gap-1">
                    <span className="text-[#BA0C2F]">*</span>
                    {Object.keys(errors).length > 0 && errors[dataField.key] && (
                      <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
                    )}
                  </div>
                )}
              </label>
            )}
            <Select
              values={value || defaultValue}
              multi={!dataField.single}
              contentRenderer={customContentRenderer}
              dropdownRenderer={(props) => (
                <div
                  className={
                    dataField.single
                      ? "react-dropdown-select-dropdown no-border"
                      : "react-dropdown-select-dropdown"
                  }
                >
                  {customDropdownRenderer(props)}
                </div>
              )}
              options={
                (dataField.single &&
                  (!dataField.options || dataField.options.length === 0) &&
                  people) ||
                dataField.options
              }
              searchFn={onSearch}
              clearable={
                (type === "audit_filters_my_tasks" && value && true) || false
              }
              disabled={dataField.disabled}
              onClearAll={() => onChange(null)}
              onChange={(value) => {
                if (dataField.single) {
                  dispatch(clearSearch());
                }
                setFieldValue(value)
              }}
              loading={status === "LOADING"}
              loadingRenderer={shouldHideLoadingIndicator ? () => null : () => <Loading />}
            />
            {dataField.notice && (
              <div className="text-sm text-[#6B7280]">{dataField.notice}</div>
            )}
          </div>
        )}
    </>
  );
}

export default PeoplePicker;
